/**
 * F-301 Texas City Override API client (AC-1 / AC-2 / AC-3).
 *
 * Consumes the canonical city-overrides route group mounted at
 * `/api/texas/city-overrides/*` and `/texas/city-overrides/*` in
 * `landlord-pro/src/routes/city-overrides.js`:
 *   GET /meta                  (city-overrides.js:67)
 *   GET /base-rules            (city-overrides.js:92)
 *   GET /                      (city-overrides.js:104)
 *   POST /                     (city-overrides.js:124)
 *   GET /resolve/:city         (city-overrides.js:148)
 *   GET /resolve/:city/required (city-overrides.js:173)
 *
 * Unlike the flood group this client is tenant-explicit: records are always
 * scoped to req.user.tenantId server-side, so the client only supplies the
 * record fields themselves. All calls use `landlordApi`; every method is
 * fail-soft and resolves to a safe default instead of throwing.
 */

import { landlordApi } from './api';

export type OverrideAction = 'SUPPLEMENT' | 'REPLACE' | 'RESTRICT';
export type OverrideLevel = 'federal' | 'state' | 'county' | 'city' | 'district';

export const OVERRIDE_ACTIONS: OverrideAction[] = ['SUPPLEMENT', 'REPLACE', 'RESTRICT'];
export const PRIORITY_CHAIN: OverrideLevel[] = ['federal', 'state', 'county', 'city', 'district'];

export interface CityOverrideMeta {
    actions: OverrideAction[];
    actionSemantics: Record<string, string>;
    priorityChain: OverrideLevel[];
    priorityNote: string;
    baseRuleCount: number;
    baseRuleSource: string;
}

export interface BaseRule {
    ruleId: string;
    label: string;
    required: boolean;
    camelKey: string;
    description: string;
    level: OverrideLevel;
    source: string;
}

export interface CityOverride {
    pk: string;
    sk: string;
    entityType: string;
    overrideId: string;
    tenantId: string;
    city: string;
    stateRuleId: string;
    action: OverrideAction;
    level: OverrideLevel;
    payload: Record<string, unknown> | null;
    ordinanceRef: string | null;
    notes: string | null;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
}

export interface EffectiveRule {
    ruleId: string;
    label: string;
    required: boolean;
    camelKey: string;
    description: string;
    level: OverrideLevel;
    source: string;
    ordinanceRef?: string | null;
    replacesRuleId?: string;
}

export interface ResolvedOverride {
    ruleId: string;
    action: OverrideAction;
    level: OverrideLevel;
    overrideId: string;
}

export interface SuppressedRule {
    ruleId: string;
    level: OverrideLevel;
    overrideId: string;
}

export interface ResolvedCitySet {
    city: string;
    chain: OverrideLevel[];
    baseRuleCount: number;
    effectiveRules: EffectiveRule[];
    applied: ResolvedOverride[];
    suppressed: SuppressedRule[];
}

export interface RequiredFieldsResult {
    city: string;
    required: string[];
    total: number;
}

/** Create-override input; mirror of validateOverrideInput() on the backend. */
export interface CityOverrideInput {
    city: string;
    stateRuleId: string;
    action: OverrideAction;
    level: OverrideLevel;
    payload?: Record<string, unknown> | null;
    ordinanceRef?: string | null;
    notes?: string | null;
    createdBy?: string;
}

function payloadOf<T>(res: any, fallback: T): T {
    try {
        const body = res && res.data;
        if (body && typeof body === 'object' && 'data' in body) {
            return (body.data ?? fallback) as T;
        }
        return (body ?? fallback) as T;
    } catch {
        return fallback;
    }
}

const EMPTY_META: CityOverrideMeta = {
    actions: OVERRIDE_ACTIONS,
    actionSemantics: {},
    priorityChain: PRIORITY_CHAIN,
    priorityNote: '',
    baseRuleCount: 0,
    baseRuleSource: '',
};

export const cityOverridesApi = {
    /** GET /meta — actions, priority chain, base registry size. */
    getMeta: async (): Promise<CityOverrideMeta> => {
        try {
            const res = await landlordApi.get('api/texas/city-overrides/meta');
            return payloadOf<CityOverrideMeta>(res, EMPTY_META);
        } catch {
            return EMPTY_META;
        }
    },

    /** GET /base-rules — the statewide F-301 registry overrides resolve against. */
    getBaseRules: async (): Promise<BaseRule[]> => {
        try {
            const res = await landlordApi.get('api/texas/city-overrides/base-rules');
            const data = payloadOf<{ rules?: BaseRule[]; total?: number }>(res, {});
            return Array.isArray(data.rules) ? data.rules : [];
        } catch {
            return [];
        }
    },

    /** GET /?city= — the tenant's raw override records, optionally scoped. */
    listOverrides: async (city?: string): Promise<CityOverride[]> => {
        try {
            const res = await landlordApi.get('api/texas/city-overrides/', {
                params: city ? { city } : undefined,
            });
            const data = payloadOf<{ overrides?: CityOverride[]; total?: number }>(res, {});
            return Array.isArray(data.overrides) ? data.overrides : [];
        } catch {
            return [];
        }
    },

    /** POST / — create one override record. Returns null on validation/store error. */
    createOverride: async (input: CityOverrideInput): Promise<CityOverride | null> => {
        try {
            const res = await landlordApi.post('api/texas/city-overrides/', input);
            return payloadOf<CityOverride | null>(res, null);
        } catch {
            return null;
        }
    },

    /** GET /resolve/:city — effective rule set after AC-1/2/3 resolution. */
    resolveCity: async (city: string): Promise<ResolvedCitySet | null> => {
        try {
            const res = await landlordApi.get(
                `api/texas/city-overrides/resolve/${encodeURIComponent(city)}`
            );
            return payloadOf<ResolvedCitySet | null>(res, null);
        } catch {
            return null;
        }
    },

    /** GET /resolve/:city/required — required-completeness view. */
    requiredFields: async (city: string): Promise<RequiredFieldsResult | null> => {
        try {
            const res = await landlordApi.get(
                `api/texas/city-overrides/resolve/${encodeURIComponent(city)}/required`
            );
            return payloadOf<RequiredFieldsResult | null>(res, null);
        } catch {
            return null;
        }
    },
};

export default cityOverridesApi;